import { Static, Type as t } from '@sinclair/typebox';
import { BaseSuccessResponseSchema, PaginatedResponseSchema } from './base';

// Update Onboarding Status
export type UpdateOnboardingStatusResponse = Static<typeof UpdateOnboardingStatusResponseSchema>;

export const UpdateOnboardingStatusResponseSchema = t.Object({
  success: t.Literal(true),
  message: t.String(),
});

// Soft Delete User
export type SoftDeleteUserResponse = Static<typeof SoftDeleteUserResponseSchema>;

export const SoftDeleteUserResponseSchema = t.Object({
  success: t.Literal(true),
  message: t.String(),
});

// Update Display Name
export type UpdateDisplayNameRequest = Static<typeof UpdateDisplayNameRequestSchema>;
export type UpdateDisplayNameResponse = Static<typeof UpdateDisplayNameResponseSchema>;

export const UpdateDisplayNameRequestSchema = t.Object({
  displayName: t.String({
    error: 'displayName is required',
  }),
});

export const UpdateDisplayNameResponseSchema = t.Object({
  message: t.String(),
  uniqId: t.String(),
});

// Update Date of Birth
export type UpdateDateOfBirthRequest = Static<typeof UpdateDateOfBirthRequestSchema>;
export type UpdateDateOfBirthResponse = Static<typeof UpdateDateOfBirthResponseSchema>;

export const UpdateDateOfBirthRequestSchema = t.Object({
  dateOfBirth: t.String({
    error: 'dateOfBirth is required',
  }),
});

export const UpdateDateOfBirthResponseSchema = t.Object({
  message: t.String(),
  dateOfBirth: t.String(),
});

// Get Counts (Love, Connections, Views) - shared query schema
export type GetUserCountQueryRequest = Static<typeof GetUserCountQueryRequestSchema>;

export const GetUserCountQueryRequestSchema = t.Optional(
  t.Object({
    userId: t.Optional(t.String()),
  }),
);

// Get Love Count
export type GetLoveCountResponse = Static<typeof GetLoveCountResponseSchema>;

export const GetLoveCountResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Object({
    totalLovesCount: t.Number(),
  }),
});

// Get Connections Count
export type GetConnectionsCountResponse = Static<typeof GetConnectionsCountResponseSchema>;

export const GetConnectionsCountResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Object({
    totalConnectionsCount: t.Number(),
  }),
});

// Get View Count
export type GetViewCountResponse = Static<typeof GetViewCountResponseSchema>;

export const GetViewCountResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Object({
    totalViewsCount: t.Number(),
  }),
});

// Get Topic List
export type GetTopicListQueryRequest = Static<typeof GetTopicListQueryRequestSchema>;
export type GetTopicListResponse = Static<typeof GetTopicListResponseSchema>;

export const GetTopicListQueryRequestSchema = t.Optional(
  t.Object({
    userId: t.Optional(t.String()),
    take: t.Optional(t.String()),
    skip: t.Optional(t.String()),
  }),
);

export const TopicItemSchema = t.Object({
  topicId: t.String(),
  topicName: t.String(),
  emoji: t.Optional(t.String()),
  content: t.String(),
});

export const GetTopicListResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Object({
    list: t.Array(TopicItemSchema),
    total: t.Number(),
  }),
});

// Get Personal Summary
export type GetPersonalSummaryQueryRequest = Static<typeof GetPersonalSummaryQueryRequestSchema>;
export type GetPersonalSummaryResponse = Static<typeof GetPersonalSummaryResponseSchema>;

export const GetPersonalSummaryQueryRequestSchema = t.Optional(
  t.Object({
    userId: t.Optional(t.String()),
  }),
);

export const GetPersonalSummaryResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Union([
    t.Object({
      shouldDisplayEmptyState: t.Literal(true),
      emptyStateText: t.String(),
    }),
    t.Object({
      shouldDisplayEmptyState: t.Literal(false),
      personalSummary: t.String(),
    }),
  ]),
});

// Get Activity Logs
export type GetActivityLogsQueryRequest = Static<typeof GetActivityLogsQueryRequestSchema>;
export type GetActivityLogsResponse = Static<typeof GetActivityLogsResponseSchema>;

export const GetActivityLogsQueryRequestSchema = t.Optional(
  t.Object({
    userId: t.Optional(t.String()),
    take: t.Optional(t.String()),
    skip: t.Optional(t.String()),
  }),
);

// Activity log structure - keeping as t.Any() for now since it's complex
export const GetActivityLogsResponseSchema = t.Object({
  success: t.Literal(true),
  data: t.Any(), // Complex inbox message structure
});
export enum UserRelationship {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTED = 'CONNECTED',
  REQUEST_SENT = 'REQUEST_SENT',
  REQUEST_RECEIVED = 'REQUEST_RECEIVED',
  INVALID = 'INVALID',
}

export const UserRelationshipSchema = t.Enum(UserRelationship);

export const UserSchema = t.Object({
  id: t.String(),
  displayName: t.String(),
  uniqId: t.String(),
  profilePic: t.Optional(t.String()),
  sharedConnectionCount: t.Optional(t.Number()),
  sharedContactCount: t.Optional(t.Number()),
  userRelationship: UserRelationshipSchema,
  isDeleted: t.Boolean(),
  totalConnectionsCount: t.Optional(t.Number()),
});

export const PaginatedUsersSchema = PaginatedResponseSchema(UserSchema);

export type PaginatedUsers = typeof PaginatedUsersSchema.static;

export const UserLookupResponseSchema = BaseSuccessResponseSchema(UserSchema);

export type UserLookupResponse = typeof UserLookupResponseSchema.static;
